/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React from "react";

import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import { isEmpty } from "lodash";

import { Pivot, PivotItem, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";

import * as ApplicationActions from "../../Dataflows/Application/ApplicationAction";

import {
  getLoadSelectedApplicationError,
  getSelectedApplication,
  getSelectedApplicationEmail,
  isLoadingSelectedApplication,
} from "../../Dataflows/Application/ApplicationSelector";
import { AppState } from "../../Dataflows/Reducers";
import { IDisplayableField } from "../../Interfaces/IDisplayableField";
import {
  eventStackStyles,
  eventStackTokens,
  labelTextStyles,
  spinnerStyles,
} from "./ApplicationDetails.styles";
import { IApplicationDetailProps } from "./IApplicationDetailProps";

const applicationFields = [
  {
    name: "id",
    label: "ID",
    type: "number",
  },
  {
    name: "firstName",
    label: "First Name",
    type: "string",
  },
  {
    name: "lastName",
    label: "Last Name",
    type: "string",
  },
  {
    name: "primaryEmail",
    label: "Email Address",
    type: "string",
  },
  {
    name: "formStatusName",
    label: "Application Status",
    type: "string",
  },
] as IDisplayableField[];

class ApplicationDetails extends React.Component<IApplicationDetailProps> {
  componentDidMount(): void {
    const { selectedApplicationEmail, actions } = this.props;
    selectedApplicationEmail && actions.loadSelectedApplication(selectedApplicationEmail);
  }

  render(): React.ReactElement<IApplicationDetailProps> {
    const { selectedApplication, isLoadingSelectedApplication, loadSelectedApplicationError } =
      this.props;

    const getFieldValue = (field: IDisplayableField): any => {
      const fieldValue = (selectedApplication as any)[field.name];

      if (isEmpty(String(fieldValue)) || fieldValue === null) return "---";

      return fieldValue;
    };

    if (selectedApplication.email == "") {
      return <p>There is no information associated with this application.</p>;
    }

    if (isLoadingSelectedApplication) {
      return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
    } else if (loadSelectedApplicationError !== "") {
      return <div>{loadSelectedApplicationError}</div>;
    }

    return (
      <>
        <Pivot aria-label="Application details">
          <PivotItem headerText="Details">
            <Stack tokens={eventStackTokens} styles={eventStackStyles}>
              {applicationFields.map((field) => {
                return (
                  <Stack horizontal key={field.name}>
                    <Text styles={labelTextStyles}>{field.label}:</Text>
                    <Text>{getFieldValue(field)}</Text>
                  </Stack>
                );
              })}
            </Stack>
          </PivotItem>
        </Pivot>
      </>
    );
  }
}

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IApplicationDetailProps & any) => ({
  isLoadingSelectedApplication: isLoadingSelectedApplication(state.applicationState),
  loadSelectedApplicationError: getLoadSelectedApplicationError(state.applicationState),
  selectedApplicationEmail: getSelectedApplicationEmail(state.applicationState),
  selectedApplication: getSelectedApplication(state.applicationState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<ApplicationActions.ApplicationActionTypes>) => ({
  actions: {
    loadSelectedApplication: bindActionCreators(ApplicationActions.loadApplication, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationDetails);
